import { useState } from "react";
import {
  Modal,
  Stack,
  Text,
  Button,
  Group,
  TextInput,
  Textarea,
  Checkbox,
  FileButton,
  Box,
  Image,
  Alert,
  Progress,
} from "@mantine/core";
import {
  IconPhoto,
  IconUpload,
  IconAlertCircle,
  IconX,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { caseImagesService } from "../../services";

interface ImageUploadModalProps {
  opened: boolean;
  onClose: () => void;
  caseId: string;
  onUploadSuccess: () => void;
}

export default function ImageUploadModal({
  opened,
  onClose,
  caseId,
  onUploadSuccess,
}: ImageUploadModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [caption, setCaption] = useState("");
  const [magnification, setMagnification] = useState("");
  const [includeInReport, setIncludeInReport] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const maxSizeBytes = 50 * 1024 * 1024; // 50 MB
  const acceptedTypes = ["image/jpeg", "image/png", "image/tiff", "image/jpg"];

  const handleFileSelect = (selectedFile: File | null) => {
    if (!selectedFile) return;

    // Validate file type
    if (!acceptedTypes.includes(selectedFile.type)) {
      notifications.show({
        title: "Invalid file type",
        message: "Please upload a JPG, PNG, or TIFF image",
        color: "red",
      });
      return;
    }

    // Validate file size
    if (selectedFile.size > maxSizeBytes) {
      notifications.show({
        title: "File too large",
        message: "Image must be less than 50 MB",
        color: "red",
      });
      return;
    }

    setFile(selectedFile);
    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleRemoveFile = () => {
    setFile(null);
    setPreview(null);
  };

  const handleClose = () => {
    if (!uploading) {
      setFile(null);
      setPreview(null);
      setCaption("");
      setMagnification("");
      setIncludeInReport(true);
      setError(null);
      setUploadProgress(0);
      onClose();
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select an image to upload");
      return;
    }

    setUploading(true);
    setUploadProgress(10);
    setError(null);

    try {
      // Get presigned URL using original filename
      setUploadProgress(20);
      const { url, key } = await caseImagesService.getPresignedUrl(
        file.name,
        file.type
      );

      // Upload to S3
      setUploadProgress(40);
      await caseImagesService.uploadToS3(url, file);

      // Create case image record (send original filename -- backend generates display name)
      setUploadProgress(70);
      await caseImagesService.createImage(caseId, {
        imageUrl: key,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        caption: caption.trim() || undefined,
        magnification: magnification.trim() || undefined,
        includeInReport,
      });

      setUploadProgress(100);

      notifications.show({
        title: "Success",
        message: "Image uploaded successfully",
        color: "green",
      });

      // Reset form and close
      handleClose();
      onUploadSuccess();
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Failed to upload image"
      );
      notifications.show({
        title: "Upload failed",
        message: "Failed to upload image. Please try again.",
        color: "red",
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      handleFileSelect(droppedFiles[0]);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconPhoto size={20} />
          <Text fw={600}>Add Image to Case</Text>
        </Group>
      }
      centered
      size="lg"
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={18} />}
            color="red"
            variant="light"
            withCloseButton
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* File Upload Area */}
        <Box>
          {!file ? (
            <Box
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              style={{
                border: isDragging
                  ? "2px dashed var(--mantine-color-blue-5)"
                  : "2px dashed var(--mantine-color-gray-4)",
                borderRadius: "8px",
                padding: "32px",
                textAlign: "center",
                backgroundColor: isDragging
                  ? "var(--mantine-color-blue-0)"
                  : "var(--mantine-color-gray-0)",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              <Stack gap="md" align="center">
                <IconUpload
                  size={48}
                  color={
                    isDragging
                      ? "var(--mantine-color-blue-5)"
                      : "var(--mantine-color-gray-6)"
                  }
                />
                <div>
                  <Text size="sm" fw={500} mb={4}>
                    {isDragging
                      ? "Drop image here"
                      : "Drop image here or click to browse"}
                  </Text>
                  <Text size="xs" c="dimmed">
                    Supported formats: JPG, PNG, TIFF (max 50 MB)
                  </Text>
                </div>
                <FileButton
                  onChange={handleFileSelect}
                  accept="image/jpeg,image/png,image/tiff,image/jpg"
                >
                  {(props) => (
                    <Button {...props} variant="light" size="sm">
                      Choose File
                    </Button>
                  )}
                </FileButton>
              </Stack>
            </Box>
          ) : (
            <Box style={{ position: "relative" }}>
              <Image
                src={preview}
                alt="Preview"
                fit="contain"
                height={300}
                style={{
                  border: "1px solid var(--mantine-color-gray-3)",
                  borderRadius: "8px",
                }}
              />
              <Button
                variant="filled"
                color="red"
                size="xs"
                leftSection={<IconX size={14} />}
                onClick={handleRemoveFile}
                style={{ position: "absolute", top: 8, right: 8 }}
                disabled={uploading}
              >
                Remove
              </Button>
              <Text size="xs" c="dimmed" mt={4}>
                {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </Text>
            </Box>
          )}
        </Box>

        {/* Caption */}
        <Textarea
          label="Caption"
          placeholder="e.g., Adenocarcinoma infiltrating muscularis propria (H&E, 40x)"
          value={caption}
          onChange={(e) => setCaption(e.currentTarget.value)}
          disabled={uploading}
          minRows={2}
        />

        {/* Magnification */}
        <TextInput
          label="Magnification (optional)"
          placeholder="e.g., 40x, 100x, 200x"
          value={magnification}
          onChange={(e) => setMagnification(e.currentTarget.value)}
          disabled={uploading}
        />

        {/* Include in Report */}
        <Checkbox
          label="Include in final report"
          checked={includeInReport}
          onChange={(e) => setIncludeInReport(e.currentTarget.checked)}
          disabled={uploading}
        />

        {/* Upload Progress */}
        {uploading && (
          <Box>
            <Text size="xs" c="dimmed" mb={4}>
              Uploading... {uploadProgress}%
            </Text>
            <Progress value={uploadProgress} animated />
          </Box>
        )}

        {/* Actions */}
        <Group justify="flex-end" gap="sm">
          <Button variant="light" onClick={handleClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            loading={uploading}
            disabled={!file}
            leftSection={<IconUpload size={16} />}
          >
            Upload to Case
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
